import { useEffect, useRef, useState } from "react";

/**
 * LazyMount — only mounts children once they're about to enter the viewport.
 *
 * Why: React.lazy() begins fetching the chunk on first render of the lazy
 * component. Wrapping it in LazyMount keeps the chunk request deferred until
 * the user is close to it (IntersectionObserver) OR the browser is idle
 * (requestIdleCallback) — protecting first paint / FCP.
 *
 * Props:
 *  - rootMargin: how early to pre-mount (default "300px")
 *  - whenIdle: also mount when the browser is idle (useful for top-of-fold
 *    decorative widgets like the hero canvas, so first paint completes first)
 *  - minHeight: optional placeholder height to avoid layout shift
 *  - fallback: optional fallback while not mounted
 */
export default function LazyMount({
  children,
  rootMargin = "300px",
  whenIdle = false,
  minHeight,
  fallback = null,
}) {
  const ref = useRef(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (mounted) return;
    const el = ref.current;
    if (!el) return;

    let idleHandle;
    const mount = () => setMounted(true);

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            mount();
            io.disconnect();
            return;
          }
        }
      },
      { rootMargin }
    );
    io.observe(el);

    if (whenIdle) {
      const ric = window.requestIdleCallback || ((cb) => setTimeout(cb, 800));
      idleHandle = ric(() => {
        mount();
        io.disconnect();
      }, { timeout: 1500 });
    }

    return () => {
      io.disconnect();
      if (whenIdle && idleHandle != null) {
        const cic = window.cancelIdleCallback || clearTimeout;
        cic(idleHandle);
      }
    };
  }, [mounted, rootMargin, whenIdle]);

  return (
    <div ref={ref} style={minHeight ? { minHeight } : undefined}>
      {mounted ? children : fallback}
    </div>
  );
}
